import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-loading';
import {
  SurveyLocationGroup,
  SurveyLocationUploadResult,
} from '@ps/survey-utils';

import {
  DownloadValidatedLocationFileResponseDto,
  LocationUploadResponseDto,
} from './dtos/location-upload.dto';
import { LocationGroupDto } from './dtos/location-group.dto';
import { LocationGroupMapper } from './mappers/location-group.mapper';
import { LocationUploadMapper } from './mappers/location-upload.mapper';
import { LocationUploadParamsMapper } from './mappers/location-upload.params.mapper';

@Injectable({ providedIn: 'root' })
export class LocationApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._env.pureSpectrum.url;
  private readonly _uploadMapper = new LocationUploadMapper();
  private readonly _paramsMapper = new LocationUploadParamsMapper();
  private readonly _groupMapper = new LocationGroupMapper();

  public locationGroups(
    surveyId: number,
    qualificationCode: number,
  ): Observable<SurveyLocationGroup[]> {
    return this._http
      .get<
        LocationGroupDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/location-groups/${qualificationCode}`)
      .pipe(map((dtos) => this._groupMapper.fromDtoList(dtos)));
  }

  public uploadLocationCodes(
    countryCode: string,
    surveyId: number,
    qualificationCode: number,
    file: File,
  ): Observable<SurveyLocationUploadResult> {
    const formData = new FormData();
    formData.append('zipcodes', file);

    const params = this._paramsMapper.toHttpParams(
      surveyId,
      countryCode,
      qualificationCode,
    );

    return this._http
      .post<LocationUploadResponseDto>(
        `${this._baseUrl}/survey/uploadZipCode/${countryCode}`,
        formData,
        { params, context: withLoading('page') },
      )
      .pipe(map((dto) => this._uploadMapper.fromDto(dto)));
  }

  public validatedLocationFileName(
    filePath: string,
    correctCodes: string[],
  ): Observable<string> {
    return this._http
      .post<DownloadValidatedLocationFileResponseDto>(
        `${this._baseUrl}/survey/downloadZipFileWithValidInvalidZip`,
        { filePath, correctZips: correctCodes },
        { context: withLoading('page') },
      )
      .pipe(map((dto) => dto.fileName));
  }

  public downloadReportFile(fileName: string): Observable<Blob> {
    return this._http.get(
      `${this._baseUrl}/reports/download-report/${fileName}`,
      { responseType: 'blob', context: withLoading('page') },
    );
  }
}
